import { useCallback, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  getClosedNoNewBusinessValue,
  getPastDueStatus,
  type PastDueOwner,
  type PastDueStatusResult,
} from '@/hooks/usePastDueStatus';
import { accountByIdQueryOptions } from '@/services/accounts';
import { useAccessToken } from '@/services/auth';

export const SHIPMENT_PAST_DUE_RESTRICTION_FLAG =
  'Shipping_Past_Due_Restriction';

export type RestrictableAccount = {
  aging60?: number | boolean | null;
  aging180?: number | boolean | null;
  aging365?: number | boolean | null;
  accountStatus?: number | null;
  tog90daysPastDue?: number | null;
  tog90DayOverrideExpireDate?: string | null;
};

export type RestrictableOwnerRef =
  | ({
      id?: string | null;
      code?: string | null;
      name?: string | null;
    } | null)
  | undefined;

export type PastDueRestrictionResult = {
  blocked: boolean;
  accountNames: string[];
  verificationFailed: boolean;
};

const toPastDueOwner = (account: RestrictableAccount): PastDueOwner => ({
  aging60: !!account.aging60,
  aging180: !!account.aging180,
  aging365: !!account.aging365,
  accountStatus: account.accountStatus ?? null,
  tog90daysPastDue: account.tog90daysPastDue ?? null,
  tog90DayOverrideExpireDate: account.tog90DayOverrideExpireDate ?? null,
});

export const getAccountPastDueStatus = (
  account: RestrictableAccount | null | undefined,
  closedNoNewBusinessValue: number | null,
): PastDueStatusResult =>
  getPastDueStatus(
    toPastDueOwner(account ?? {}),
    [],
    closedNoNewBusinessValue,
    true,
  );

export const isAccountPastDueRestricted = (
  account: RestrictableAccount | null | undefined,
  closedNoNewBusinessValue: number | null,
): boolean =>
  !!account &&
  getAccountPastDueStatus(account, closedNoNewBusinessValue).isLocked;

export function useShipmentPastDueRestriction() {
  const restrictionEnabled = useFeatureFlag(SHIPMENT_PAST_DUE_RESTRICTION_FLAG);
  const queryClient = useQueryClient();
  const token = useAccessToken();
  const closedNoNewBusinessValue = useMemo(
    () => getClosedNoNewBusinessValue(),
    [],
  );

  const isOwnerRestricted = useCallback(
    (account: RestrictableAccount | null | undefined) =>
      restrictionEnabled &&
      isAccountPastDueRestricted(account, closedNoNewBusinessValue),
    [closedNoNewBusinessValue, restrictionEnabled],
  );

  const getOwnerStatus = useCallback(
    (account: RestrictableAccount | null | undefined) =>
      getAccountPastDueStatus(account, closedNoNewBusinessValue),
    [closedNoNewBusinessValue],
  );

  const checkOwners = useCallback(
    async (
      owners: RestrictableOwnerRef[],
    ): Promise<PastDueRestrictionResult> => {
      const notBlocked: PastDueRestrictionResult = {
        blocked: false,
        accountNames: [],
        verificationFailed: false,
      };

      if (!restrictionEnabled) {
        return notBlocked;
      }

      const ownerNamesById = new Map<string, string>();
      for (const owner of owners) {
        if (owner?.id) {
          ownerNamesById.set(owner.id, owner.name ?? owner.code ?? owner.id);
        }
      }

      if (ownerNamesById.size === 0) {
        return notBlocked;
      }

      try {
        const accounts = await Promise.all(
          [...ownerNamesById.keys()].map((id) =>
            queryClient.fetchQuery(accountByIdQueryOptions(token, id)),
          ),
        );

        const accountNames = accounts
          .filter((account) =>
            isAccountPastDueRestricted(account, closedNoNewBusinessValue),
          )
          .map(
            (account) =>
              account?.name ??
              account?.code ??
              (account?.id ? ownerNamesById.get(account.id) : null) ??
              '',
          );

        return {
          blocked: accountNames.length > 0,
          accountNames,
          verificationFailed: false,
        };
      } catch (error) {
        console.error('Failed to verify account past due status', error);
        return {
          blocked: true,
          accountNames: [...ownerNamesById.values()],
          verificationFailed: true,
        };
      }
    },
    [closedNoNewBusinessValue, queryClient, restrictionEnabled, token],
  );

  return { restrictionEnabled, isOwnerRestricted, getOwnerStatus, checkOwners };
}
